import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Icon,
  Input,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { useQueryClient } from 'react-query';

import {
  FuelCalibration,
  FuelPump,
  fuelErrorMessage,
  fuelReadingsService,
} from '../../../services/fuelReadingsService';
import {
  CALIBRATION_MAX_OVER_ML,
  CALIBRATION_MAX_UNDER_ML,
  CALIBRATION_VOLUME,
  calibrationApproved,
  formatDateTime,
  formatDeviation,
} from '../../../utils/fuelReadings';
import { PhotoInput } from './PhotoInput';

// aferição (PRO PA nº 003): enche a medida padrão de 20 l com a bomba e lê a
// régua do medidor. de -60 a +100 ml está aprovada.
export function CalibrationForm({ pumps }: { pumps: FuelPump[] }) {
  const queryClient = useQueryClient();
  const [pumpId, setPumpId] = useState('');
  const [sign, setSign] = useState<1 | -1>(1);
  const [value, setValue] = useState('');
  const [photo, setPhoto] = useState<Blob | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState<FuelCalibration | null>(null);

  useEffect(() => {
    if (!pumpId && pumps.length > 0) {
      setPumpId(pumps[0].id);
    }
  }, [pumpId, pumps]);

  const pump = pumps.find(item => item.id === pumpId);
  const ml = /^\d{1,4}$/.test(value.trim()) ? sign * Number(value) : NaN;
  const valid = Number.isFinite(ml);
  const approved = valid && calibrationApproved(ml);
  let resultColor = 'gray.500';

  if (valid) {
    resultColor = approved ? 'green.400' : 'red.400';
  }

  const submit = async () => {
    if (!pump || !valid) {
      return;
    }

    setSaving(true);
    setError('');
    try {
      const result = await fuelReadingsService.createCalibration({
        pump_id: pump.id,
        deviation_ml: String(ml),
        photo,
        notes: notes.trim() || undefined,
      });

      setSaved(result);
      setValue('');
      setSign(1);
      setPhoto(null);
      setNotes('');
      await queryClient.invalidateQueries('fuel-readings');
    } catch (err) {
      setError(fuelErrorMessage(err, 'Não foi possível salvar a aferição.'));
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <Stack spacing="4">
        <Box
          bg={saved.approved ? 'green.50' : 'red.50'}
          color={saved.approved ? 'green.900' : 'red.900'}
          borderWidth="1px"
          borderColor={saved.approved ? 'green.300' : 'red.300'}
          borderRadius="12px"
          p="5"
          textAlign="center"
        >
          <Icon
            as={saved.approved ? FiCheckCircle : FiXCircle}
            boxSize="12"
            color={saved.approved ? 'green.500' : 'red.500'}
          />
          <Text fontWeight="bold" fontSize="lg" mt="2">
            Aferição {saved.approved ? 'aprovada' : 'reprovada'}
          </Text>
          <Text>
            {saved.pump_name}: {saved.volume_liters} L, régua{' '}
            <b>{formatDeviation(saved.deviation_ml)}</b>
          </Text>
          <Text fontSize="sm" mt="1">
            {formatDateTime(saved.calibrated_at, true)}
          </Text>
          {!saved.approved && (
            <Text fontSize="sm" mt="3" fontWeight="medium">
              Bomba desregulada: avise o encarregado. Pelo procedimento ela não
              deve ser usada até ser calibrada.
            </Text>
          )}
        </Box>
        <Button size="lg" variant="outline" onClick={() => setSaved(null)}>
          Nova aferição
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing="5">
      <Text fontSize="sm" color="gray.500">
        Encha a medida padrão de {CALIBRATION_VOLUME} L com a bomba e veja onde
        o diesel parou na régua do medidor. Os {CALIBRATION_VOLUME} L voltam
        para o tanque e saem do total bombeado do dia.
      </Text>

      <Box>
        <Text fontWeight="medium" mb="2">
          1. Bomba
        </Text>
        <ButtonGroup isAttached w="100%">
          {pumps.map(item => (
            <Button
              key={item.id}
              flex="1"
              size="lg"
              colorScheme={item.id === pumpId ? 'blue' : 'gray'}
              variant={item.id === pumpId ? 'solid' : 'outline'}
              onClick={() => setPumpId(item.id)}
            >
              {item.name}
            </Button>
          ))}
        </ButtonGroup>
        {pump && (
          <Text fontSize="sm" color="gray.500" mt="2">
            {pump.last_calibration_at
              ? `Última aferição: ${formatDateTime(
                  pump.last_calibration_at,
                  true,
                )} (${
                  pump.last_calibration_approved ? 'aprovada' : 'reprovada'
                })`
              : 'Ainda sem aferição registrada.'}
          </Text>
        )}
      </Box>

      <Box>
        <Text fontWeight="medium" mb="2">
          2. Onde parou na régua (ml)
        </Text>
        <ButtonGroup isAttached w="100%" mb="2">
          <Button
            flex="1"
            colorScheme={sign === -1 ? 'blue' : 'gray'}
            variant={sign === -1 ? 'solid' : 'outline'}
            onClick={() => setSign(-1)}
          >
            − Abaixo do zero
          </Button>
          <Button
            flex="1"
            colorScheme={sign === 1 ? 'blue' : 'gray'}
            variant={sign === 1 ? 'solid' : 'outline'}
            onClick={() => setSign(1)}
          >
            + Acima do zero
          </Button>
        </ButtonGroup>
        <Flex align="center" gridGap="2">
          <Input
            value={value}
            onChange={event => setValue(event.target.value)}
            inputMode="numeric"
            autoComplete="off"
            placeholder="0"
            size="lg"
            fontSize="2xl"
            textAlign="center"
            fontFamily="mono"
            h="16"
            isInvalid={!!value && !valid}
            aria-label="Régua em ml"
          />
          <Text fontSize="xl" color="gray.500">
            ml
          </Text>
        </Flex>
        <Text fontSize="sm" mt="2" textAlign="center" color={resultColor}>
          {!valid &&
            `Aprovada de ${CALIBRATION_MAX_UNDER_ML} ml a +${CALIBRATION_MAX_OVER_ML} ml. Exatamente no zero: digite 0.`}
          {valid &&
            approved &&
            `${formatDeviation(ml)}: dentro da tolerância (aprovada).`}
          {valid &&
            !approved &&
            `${formatDeviation(
              ml,
            )}: fora da tolerância. A bomba precisa ser calibrada.`}
        </Text>
      </Box>

      <Box>
        <Text fontWeight="medium" mb="2">
          3. Foto da régua (opcional)
        </Text>
        <PhotoInput
          label="Tirar foto da régua"
          value={photo}
          onChange={setPhoto}
          onPreparing={setPreparing}
        />
      </Box>

      <Box>
        <Text fontSize="sm" mb="1" color="gray.500">
          Ocorrência (opcional)
        </Text>
        <Textarea
          value={notes}
          onChange={event => setNotes(event.target.value)}
          maxLength={500}
          rows={2}
          aria-label="Ocorrência da aferição"
        />
      </Box>

      {error && (
        <Alert status="error" color="red.900" borderRadius="md">
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button
        colorScheme="blue"
        size="lg"
        h="14"
        onClick={submit}
        isLoading={saving}
        loadingText="Salvando..."
        isDisabled={!pump || !valid || preparing}
      >
        Salvar aferição
      </Button>
    </Stack>
  );
}
